import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { withStyles } from "@mui/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CustomAdvancedMenuList, StyledDataGrid } from "../../../components/StyledComponents";
import { useTranslation } from "react-i18next";
import { getLeaveByUser } from "../../../axios/User/UserApi";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import { toast } from "react-toastify";
import YearPickerPopup from "../../../components/YearPickerPopup";
import CustomSpinner from "../../../components/CustomSpinner";
import EditEntitlementForm from "./EditEntitlementForm";
import LeaveSummary from "./LeaveSummary";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import CustomPagination from "../CustomPagination";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";
import { FaClock } from "react-icons/fa6";
import { useAuth } from "../../../context/AuthContext";
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton } from "../../../components/StyledButton";
import DefaultTooltip, {
    LeaveUsedDaysTooltipContent,
    UnpaidLeaveHoursTooltipContent,
} from "../../../components/DefaultTooltip";
import QuestionIcon16pxSvg from "../../../assets/images/icons/QuestionIcon16pxSvg";

/** Tooltip trắng giống MoreActionTooltip */
const UnpaidMonthTooltip = withStyles({
    tooltip: {
        "&.MuiTooltip-tooltip": {
            padding: 0,
            borderRadius: "6px",
            fontFamily: "'Montserrat', san-serif",
            backgroundColor: "#FFFFFF",
            border: "1px solid rgba(0, 0, 0, 0.2)",
            maxWidth: 400,
            "& .MuiTooltip-arrow": {
                color: "#FFFFFF",
                "&:before": {
                    border: "1px solid rgba(0, 0, 0, 0.25)",
                },
            },
        },
    },
})(Tooltip);
const MONTH_KEYS = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
];

/** Hiển thị số thập phân tối đa 2 chữ số, số nguyên giữ nguyên */
const formatDecimalNumber = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return value ?? 0;
    if (Number.isInteger(numberValue)) return numberValue;
    return Number(numberValue.toFixed(2));
};

/** Làm tròn 2 chữ số thập phân (vd: 2.50, 3) */
const formatOneDecimal = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return 0;
    const rounded = Math.round(numberValue * 100) / 100;
    return Number.isInteger(rounded) ? rounded : rounded;
};

/** Parse "HH:mm:ss" → tổng giây */
const parseTimeToSeconds = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return 0;
    const parts = timeStr.trim().split(":").map(Number);
    if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return 0;
    const [h = 0, m = 0, s = 0] = parts;
    return h * 3600 + m * 60 + s;
};

/** numOfTime / hoursPerLeaveDaySnapshot → số ngày */
const calcUnpaidDaysFromTime = (numOfTime, hoursPerLeaveDaySnapshot) => {
    const unpaidSeconds = parseTimeToSeconds(numOfTime);
    const daySeconds = parseTimeToSeconds(hoursPerLeaveDaySnapshot);
    if (daySeconds <= 0) return 0;
    return unpaidSeconds / daySeconds;
};

/** Snapshot null → fallback hoursPerLeaveDay → 08:00:00 */
const resolveHoursPerLeaveDay = (item) =>
    item?.hoursPerLeaveDaySnapshot ||
    item?.hoursPerLeaveDay ||
    "08:00:00";

const secondsToHhMmSs = (totalSeconds) => {
    const secs = Math.max(0, Math.round(Number(totalSeconds) || 0));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * Số giây → chuỗi thời lượng theo genaralSettingTimeFormat công ty.
 * Bỏ AM/PM vì đây là thời lượng nghỉ, không phải giờ đồng hồ.
 */
const formatDurationByCompanyTimeFormat = (
    totalSeconds,
    timeFormat = "HH:mm:ss"
) => {
    const secs = Math.max(0, Math.round(Number(totalSeconds) || 0));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;

    const fmt =
        String(timeFormat || "HH:mm:ss")
            .replace(/\s*a\s*/gi, "")
            .trim() || "HH:mm:ss";
    const showSeconds = /ss/i.test(fmt);
    const padHour = /HH|hh/.test(fmt);

    const hh = padHour ? String(h).padStart(2, "0") : String(h);
    const mm = String(m).padStart(2, "0");
    const ss = String(s).padStart(2, "0");

    return showSeconds ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
};

/**
 * Gộp nhiều bản ghi cùng leaveTypeId:
 * usedFromYear/Carry (ngày) = SUM(time / hoursPerLeaveDaySnapshot)
 */
const aggregateLeaveByUserRows = (rows) => {
    const grouped = new Map();

    (rows || []).forEach((item) => {
        const id = item.leaveTypeId;
        if (!grouped.has(id)) {
            grouped.set(id, {
                ...item,
                usedFromYearDetails: [],
                usedFromCarryDetails: [],
                _usedDaysSum: 0,
                _carryDaysSum: 0,
                _usedSecondsSum: 0,
                _carrySecondsSum: 0,
            });
        }

        const acc = grouped.get(id);
        const hoursPerDay = resolveHoursPerLeaveDay(item);
        const usedTime = item.usedFromYear || "00:00:00";
        const carryTime = item.usedFromCarry || "00:00:00";

        if (Number(id) === 1) {
            // Leave theo giờ: cộng giây rồi quy ra số giờ hiển thị
            acc._usedSecondsSum += parseTimeToSeconds(usedTime);
            acc._carrySecondsSum += parseTimeToSeconds(carryTime);
            return;
        }

        const usedDays = calcUnpaidDaysFromTime(usedTime, hoursPerDay);
        const carryDays = calcUnpaidDaysFromTime(carryTime, hoursPerDay);
        acc._usedDaysSum += usedDays;
        acc._carryDaysSum += carryDays;
        acc.usedFromYearDetails.push({
            numOfTime: usedTime,
            hoursPerLeaveDay: hoursPerDay,
            days: formatOneDecimal(usedDays),
        });
        acc.usedFromCarryDetails.push({
            numOfTime: carryTime,
            hoursPerLeaveDay: hoursPerDay,
            days: formatOneDecimal(carryDays),
        });
    });

    return Array.from(grouped.values()).map((acc) => {
        if (Number(acc.leaveTypeId) === 1) {
            return {
                ...acc,
                usedFromYear: formatDecimalNumber(acc._usedSecondsSum / 3600),
                usedFromCarry: formatDecimalNumber(acc._carrySecondsSum / 3600),
                usedFromYearRaw: secondsToHhMmSs(acc._usedSecondsSum),
                usedFromCarryRaw: secondsToHhMmSs(acc._carrySecondsSum),
            };
        }

        return {
            ...acc,
            usedFromYear: formatOneDecimal(acc._usedDaysSum),
            usedFromCarry: formatOneDecimal(acc._carryDaysSum),
        };
    });
};

/** Tooltip cách tính giờ nghỉ không lương của một tháng */
const UnpaidHoursFormulaTooltip = ({
    numOfTime,
    hoursPerLeaveDay,
    days,
    children,
}) => (
    <UnpaidMonthTooltip
        title={
            <div className="bg-[#FFFFFF] px-3 py-2 text-xs text-[#1F2937] max-h-64 overflow-auto">
                <UnpaidLeaveHoursTooltipContent
                    numOfTime={numOfTime}
                    hoursPerLeaveDay={hoursPerLeaveDay}
                    days={days}
                />
            </div>
        }
        arrow
        placement="top"
    >
        <span className="cursor-default block w-full max-w-full overflow-hidden">
            {children}
        </span>
    </UnpaidMonthTooltip>
);

/** Tooltip giải thích số ngày = thời gian nghỉ ÷ giờ quy định 1 ngày */
const UsedTimeFormulaTooltip = ({ details, totalDays, children }) => {
    if (!details?.length) return children;

    return (
        <UnpaidMonthTooltip
            title={
                <div className="bg-[#FFFFFF] px-3 py-2 text-xs text-[#1F2937] max-h-64 overflow-auto">
                    <LeaveUsedDaysTooltipContent
                        details={details}
                        totalDays={formatOneDecimal(totalDays)}
                    />
                </div>
            }
            arrow
            placement="top"
        >
            <span className="cursor-default block w-full max-w-full overflow-hidden">
                {children}
            </span>
        </UnpaidMonthTooltip>
    );
};

const NumberCell = ({ children }) => (
    <div className="w-full max-w-full h-full flex items-center justify-center overflow-hidden tabular-nums text-sm text-[#111827]">
        {children}
    </div>
);

const DEFAULT_DAY_SECONDS = 8 * 3600;

/** Giây quy đổi 1 ngày phép từ row hoặc setting hệ thống */
const resolveDaySeconds = (row, fallbackHhMmSs) => {
    const fromRow = parseTimeToSeconds(resolveHoursPerLeaveDay(row));
    if (fromRow > 0) return fromRow;
    const fromSetting = parseTimeToSeconds(fallbackHhMmSs);
    return fromSetting > 0 ? fromSetting : DEFAULT_DAY_SECONDS;
};

/** Cộng giây từ danh sách chi tiết used*Details */
const sumDetailsSeconds = (details) =>
    (details || []).reduce(
        (sum, item) => sum + parseTimeToSeconds(item?.numOfTime),
        0
    );

/** Số giây → chuỗi giờ theo format công ty (gọn trong ô bảng) */
const formatHoursMinutesLabel = (totalSeconds, timeFormat) => {
    return formatDurationByCompanyTimeFormat(totalSeconds, timeFormat);
};

/** Ô số ngày + dòng phụ (giờ theo format công ty) */
const DaysWithHoursCell = ({ days, totalSeconds, t, timeFormat, compact = false }) => {
    const dayValue = formatOneDecimal(days ?? 0);
    const dayLabel = `${dayValue} ${t("common.day").toLowerCase()}`;
    const hoursLabel = `(${formatHoursMinutesLabel(totalSeconds, timeFormat)})`;

    return (
        <NumberCell>
            <div className="w-full max-w-full px-0.5 flex flex-col items-center justify-center leading-none gap-0.5 overflow-hidden">
                {/* nowrap + truncate: cột tháng hẹp không xuống dòng chồng lên giờ */}
                <span
                    title={dayLabel}
                    className={`block w-full h-5 font-semibold leading-5 text-[#111827] text-center whitespace-nowrap truncate ${
                        compact ? "text-xs" : "text-sm"
                    }`}
                >
                    {dayLabel}
                </span>
                <span
                    title={hoursLabel}
                    className={`block w-full h-4 font-normal text-[#6B7280] text-center tabular-nums whitespace-nowrap truncate ${
                        compact ? "text-[10px] leading-4" : "text-[11px] leading-4"
                    }`}
                >
                    {hoursLabel}
                </span>
            </div>
        </NumberCell>
    );
};

/**
 * Ô entitlement editable — giao diện giống DaysWithHoursCell ("X ngày" + giờ),
 * hover/focus vẫn tăng giảm được.
 */
const EntitlementEditableCell = ({ value, onChange, unitLabel, hoursLabel }) => {
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [inputText, setInputText] = useState(String(formatDecimalNumber(value)));
    const active = hovered || focused;
    const numberValue = Number(formatDecimalNumber(value));

    useEffect(() => {
        if (!focused) {
            setInputText(String(formatDecimalNumber(value)));
        }
    }, [value, focused]);

    const clampAndCommit = (nextRaw) => {
        let next = Number(nextRaw);
        if (!Number.isFinite(next) || next < 0) next = 0;

        const formatted = formatDecimalNumber(next);
        setInputText(String(formatted));
        onChange(formatted);
        return formatted;
    };

    const bump = (delta) => {
        const base = Number.isFinite(Number(inputText)) ? Number(inputText) : numberValue;
        const next = Math.max(0, base + delta);
        clampAndCommit(Number(next.toFixed(2)));
    };

    const inactiveWidth = Math.max(20, String(inputText || "0").length * 9 + 4);

    return (
        <NumberCell>
            <div
                className="w-full max-w-full px-1 flex flex-col items-center justify-center leading-tight gap-0.5 overflow-hidden"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {/* Dòng 1: idle h-5 giống DaysWithHoursCell; active h-8 cho nút tăng/giảm */}
                <div
                    className={`relative z-10 flex items-center justify-center gap-1 ${
                        active ? "h-8" : "h-5"
                    }`}
                >
                    <div
                        className={`relative flex items-center rounded-lg border bg-transparent transition-colors ${
                            active
                                ? "h-8 border-[#DBE8FF] bg-[#F5F8FF]"
                                : "h-5 border-transparent"
                        } ${focused ? "!border-[#0057FF] !bg-white" : ""}`}
                        style={{ width: active ? 96 : inactiveWidth }}
                    >
                        <input
                            type="text"
                            inputMode="decimal"
                            value={inputText}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={() => setFocused(true)}
                            onBlur={() => {
                                setFocused(false);
                                clampAndCommit(inputText);
                            }}
                            onChange={(e) => {
                                const next = e.target.value;
                                if (next === "" || /^-?\d*\.?\d{0,2}$/.test(next)) {
                                    setInputText(next);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.currentTarget.blur();
                                }
                                if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    bump(1);
                                }
                                if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    bump(-1);
                                }
                            }}
                            className={`entitlement-edit-input h-full bg-transparent font-semibold text-sm leading-5 text-[#111827] outline-none tabular-nums ${
                                active
                                    ? "w-full pr-6 pl-2 text-center"
                                    : "w-full px-0 text-right"
                            }`}
                        />
                        {active && (
                            <div className="absolute right-0.5 top-0.5 bottom-0.5 z-20 flex w-5 flex-col overflow-hidden rounded border border-[#E5E7EB] bg-white shadow-sm">
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        bump(1);
                                    }}
                                    className="flex h-1/2 min-h-[12px] items-center justify-center text-[#6B7280] hover:bg-[#F5F8FF] hover:text-[#0057FF]"
                                >
                                    <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                                </button>
                                <div className="h-px shrink-0 bg-[#E5E7EB]" />
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        bump(-1);
                                    }}
                                    className="flex h-1/2 min-h-[12px] items-center justify-center text-[#6B7280] hover:bg-[#F5F8FF] hover:text-[#0057FF]"
                                >
                                    <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                                </button>
                            </div>
                        )}
                    </div>
                    {unitLabel && (
                        <span className="font-semibold text-sm leading-5 text-[#111827]">
                            {unitLabel}
                        </span>
                    )}
                </div>
                {/* Ẩn dòng giờ khi đang edit để không che nút giảm */}
                {hoursLabel && !active && (
                    <span className="w-full text-[11px] leading-tight text-[#6B7280] font-normal text-center tabular-nums truncate">
                        ({hoursLabel})
                    </span>
                )}
            </div>
        </NumberCell>
    );
};

const LeaveManagement = (props) => {
    const { userId, handleRefreshTable } = props;
    const { t, i18n } = useTranslation();
    const { profile, isAdmin, numberOfHoursOneDay, genaralSettingTimeFormat } =
        useAuth();
    const { getLeaveSummary, getLeaveUnpaid, updateLeaveTypesUser } = LeaveApi();

    const [openEditEntitlement, setOpenEditEntitlement] = useState(false);

    const [openLeaveSummary, setOpenLeaveSummary] = useState(false);

    const handleOpenLeaveSummary = () => {
        setOpenLeaveSummary(true);
    };

    const handleCloseLeaveSummary = () => {
        setOpenLeaveSummary(false);
    };

    const handleCloseEditEntitlement = () => {
        setOpenEditEntitlement(false);
    };

    /* ===================== YEAR ===================== */
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);

    const years = useMemo(() => {
        const range = 5; // trước & sau 5 năm
        return Array.from(
            { length: range * 2 + 1 },
            (_, i) => currentYear - range + i
        );
    }, [currentYear]);

    /* ===================== DATA ===================== */
    const [leaveByUser, setLeaveByUserData] = useState([]);
    // Draft chỉ 2 trường cho phép update: carryIn + totalEntitlementYear
    const [draftLeaveTypes, setDraftLeaveTypes] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [balanceTimePresent, setBalanceTimePresent] = useState("00:00:00");
    const [unpaidByMonth, setUnpaidByMonth] = useState({});

    const targetUserId = isAdmin ? userId : profile?.userId;

    const buildDraftFromRow = (row) => ({
        totalEntitlementYear: formatDecimalNumber(row.totalEntitlementYear ?? 0),
        carryIn: formatDecimalNumber(row.carryIn ?? 0),
    });

    const syncDraftFromLeaveData = useCallback((rows) => {
        const nextDraft = {};
        (rows || []).forEach((row) => {
            if (row.leaveTypeId === 1) return;
            nextDraft[row.leaveTypeId] = buildDraftFromRow(row);
        });
        setDraftLeaveTypes(nextDraft);
        setIsDirty(false);
    }, []);

    const fetchLeaveByUserData = async ({ silent = false } = {}) => {
        // Chưa có userId (admin: prop chưa load / employee: profile chưa sẵn) → không gọi API
        if (!targetUserId) return;

        const payload = {
            langId: localStorage.getItem("i18nextLngId"),
            userId: targetUserId,
            year,
        };

        if (!silent) setLoading(true);

        try {
            const response = await getLeaveByUser(payload);
            if (
                response?.data?.message === "Successful" ||
                response?.data?.message === "Thành công"
            ) {
                const rows = aggregateLeaveByUserRows(response.data.data || []);
                setLeaveByUserData(rows);
                syncDraftFromLeaveData(rows);
            }
        } catch (error) {
            if (error?.response) {
                if (error.response.status !== 401) {
                    toast.error(error?.response?.data?.message || "Error");
                }
            } else {
                toast.error(t("common.network-timeout") || "Network error or server is unreachable");
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const parseApiBody = (res) => {
        let body = res?.data;
        // BE trả String body → axios có thể nhận JSON string lồng nhau
        let depth = 0;
        while (typeof body === "string" && depth < 3) {
            const trimmed = body.trim();
            if (!trimmed.startsWith("{") && !trimmed.startsWith("[") && !trimmed.startsWith("\"")) {
                break;
            }
            try {
                body = JSON.parse(body);
                depth += 1;
            } catch {
                break;
            }
        }
        return body;
    };

    const normalizeListData = (data) => {
        let list = data;
        if (typeof list === "string") {
            try {
                list = JSON.parse(list);
            } catch {
                return [];
            }
        }
        if (!list) return [];
        return Array.isArray(list) ? list : [list];
    };

    const isApiSuccess = (body) =>
        body?.message === "Successful" ||
        body?.message === "Thành công" ||
        Number(body?.responseCode) === 0;

    const fetchLeaveSummaryAndUnpaid = async () => {
        if (!targetUserId) return;

        const payload = {
            langId: localStorage.getItem("i18nextLngId"),
            userId: targetUserId,
            year,
        };

        // Gọi riêng từng API để lỗi summary không làm mất data unpaid
        try {
            const summaryRes = await getLeaveSummary(payload);
            const summaryBody = parseApiBody(summaryRes);

            if (isApiSuccess(summaryBody)) {
                const summaryData = summaryBody?.data;
                const balance = Array.isArray(summaryData)
                    ? summaryData[0]?.balanceTimePresent
                    : summaryData?.balanceTimePresent;
                setBalanceTimePresent(balance ?? "00:00:00");
            } else {
                setBalanceTimePresent("00:00:00");
            }
        } catch (error) {
            setBalanceTimePresent("00:00:00");
            if (error?.response && error.response.status !== 401) {
                const errBody = parseApiBody(error.response);
                toast.error(errBody?.message || error?.response?.data?.message || "Error");
            }
        }

        try {
            const unpaidRes = await getLeaveUnpaid(payload);
            const unpaidBody = parseApiBody(unpaidRes);

            if (isApiSuccess(unpaidBody)) {
                const monthMap = {};
                normalizeListData(unpaidBody?.data).forEach((item) => {
                    const month = Number(item?.month);
                    if (Number.isFinite(month) && month >= 1 && month <= 12) {
                        monthMap[month] = {
                            numOfTime: item?.numOfTime || "00:00:00",
                            hoursPerLeaveDay:
                                item?.hoursPerLeaveDaySnapshot ||
                                item?.hoursPerLeaveDay ||
                                "",
                        };
                    }
                });
                setUnpaidByMonth(monthMap);
            } else {
                setUnpaidByMonth({});
            }
        } catch (error) {
            setUnpaidByMonth({});
            if (error?.response && error.response.status !== 401) {
                const errBody = parseApiBody(error.response);
                toast.error(errBody?.message || error?.response?.data?.message || "Error");
            }
        }
    };

    useEffect(() => {
        fetchLeaveByUserData();
    }, [targetUserId, year]);

    useEffect(() => {
        fetchLeaveSummaryAndUnpaid();
    }, [targetUserId, year]);

    const handleLeaveTypeFieldChange = (leaveTypeId, field, rawValue) => {
        let value = rawValue;

        if (value === "" || value === null) {
            value = 0;
        }

        value = Number(value);

        if (value < 0 || Number.isNaN(value)) {
            value = 0;
        }

        setDraftLeaveTypes((prev) => ({
            ...prev,
            [leaveTypeId]: {
                ...(prev[leaveTypeId] || {}),
                [field]: formatDecimalNumber(value),
            },
        }));
        setIsDirty(true);
    };

    const isRowChanged = (row, draft) => {
        if (!draft) return false;
        return (
            Number(draft.totalEntitlementYear ?? 0) !== Number(row.totalEntitlementYear ?? 0) ||
            Number(draft.carryIn ?? 0) !== Number(row.carryIn ?? 0)
        );
    };

    const handleSaveEntitlements = async () => {
        if (!isAdmin || !isDirty || isSaving) return;

        const editableRows = leaveByUser.filter((row) => row.leaveTypeId !== 1);
        const changedRows = editableRows.filter((row) =>
            isRowChanged(row, draftLeaveTypes[row.leaveTypeId])
        );

        if (!changedRows.length) {
            setIsDirty(false);
            return;
        }

        setIsSaving(true);
        setLoading(true);

        try {
            const responses = await Promise.all(
                changedRows.map((row) => {
                    const draft = draftLeaveTypes[row.leaveTypeId] || buildDraftFromRow(row);
                    return updateLeaveTypesUser({
                        userId: targetUserId,
                        leaveTypeId: row.leaveTypeId,
                        year,
                        totalEntitlementYear: Number(draft.totalEntitlementYear ?? 0),
                        carryIn: Number(draft.carryIn ?? 0),
                        // used* giữ nguyên từ server — không cho edit trên UI
                        usedFromYear: Number(row.usedFromYear ?? 0),
                        usedFromCarry: Number(row.usedFromCarry ?? 0),
                        langId: localStorage.getItem("i18nextLngId"),
                    });
                })
            );

            const allSuccess = responses.every((res) => {
                const body = parseApiBody(res);
                return isApiSuccess(body);
            });

            if (allSuccess) {
                toast.success(
                    t("toast.update-entitlement-success") ||
                    "Update entitlement successfully"
                );
                setIsDirty(false);
                await fetchLeaveByUserData({ silent: true });
                handleRefreshTable?.();
            } else {
                const firstError = responses.find((res) => {
                    const body = parseApiBody(res);
                    return !isApiSuccess(body);
                });
                const errorBody = parseApiBody(firstError);
                toast.error(
                    errorBody?.message ||
                    t("toast.update-entitlement-failed") ||
                    "Update entitlement failed"
                );
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                t("toast.update-entitlement-failed") ||
                "Update entitlement failed"
            );
        } finally {
            setIsSaving(false);
            setLoading(false);
        }
    };

    const handleCancelEntitlements = () => {
        if (!isAdmin || isSaving) return;
        syncDraftFromLeaveData(leaveByUser);
    };

    const columns = useMemo(
        () => [
            {
                field: "leaveTypeName",
                headerName: t("leave.leave-type") || "Leave type",
                flex: 1.2,
                minWidth: 140,
                align: "left",
                headerAlign: "left",
                renderCell: (params) => {
                    const { remark, remarkEN, leaveTypeName } = params.row || {};
                    if (i18n.language === "vi") {
                        return remark || leaveTypeName || "-";
                    }
                    return remarkEN || leaveTypeName || "-";
                },
            },
            // --- Điều chuyển từ năm trước ---
            {
                field: "usedFromCarry",
                headerName: t("leave.used-from-carry"),
                flex: 1.2,
                minWidth: 150,
                align: "center",
                headerAlign: "center",
                headerClassName: "leave-col-carry",
                cellClassName: "leave-col-carry",
                renderCell: (params) => {
                    const { usedFromCarry, usedFromCarryDetails } = params.row || {};
                    const daySeconds = resolveDaySeconds(
                        params.row,
                        numberOfHoursOneDay
                    );
                    const totalSeconds =
                        sumDetailsSeconds(usedFromCarryDetails) ||
                        Math.round(Number(usedFromCarry ?? 0) * daySeconds);

                    return (
                        <UsedTimeFormulaTooltip
                            details={usedFromCarryDetails}
                            totalDays={usedFromCarry}
                        >
                            <DaysWithHoursCell
                                days={usedFromCarry}
                                totalSeconds={totalSeconds}
                                t={t}
                                timeFormat={genaralSettingTimeFormat}
                            />
                        </UsedTimeFormulaTooltip>
                    );
                },
            },
            {
                field: "carryIn",
                headerName: t("leave.carry-available-until"),
                flex: 1.2,
                minWidth: 160,
                align: "center",
                headerAlign: "center",
                headerClassName: "leave-col-carry",
                cellClassName: "leave-col-carry",
                renderCell: (params) => {
                    const { leaveTypeId, carryIn } = params.row || {};
                    const daySeconds = resolveDaySeconds(
                        params.row,
                        numberOfHoursOneDay
                    );
                    const draft = draftLeaveTypes[leaveTypeId];
                    const days = draft?.carryIn ?? carryIn ?? 0;
                    const totalSeconds = Math.round(Number(days) * daySeconds);

                    if (!isAdmin) {
                        return (
                            <DaysWithHoursCell
                                days={days}
                                totalSeconds={totalSeconds}
                                t={t}
                                timeFormat={genaralSettingTimeFormat}
                            />
                        );
                    }

                    return (
                        <EntitlementEditableCell
                            value={days}
                            unitLabel={t("common.day").toLowerCase()}
                            hoursLabel={formatHoursMinutesLabel(
                                totalSeconds,
                                genaralSettingTimeFormat
                            )}
                            onChange={(next) =>
                                handleLeaveTypeFieldChange(
                                    leaveTypeId,
                                    "carryIn",
                                    next
                                )
                            }
                        />
                    );
                },
            },
            // --- Tích lũy năm nay ---
            {
                field: "usedFromYear",
                headerName: t("leave.used-from-accrual"),
                flex: 1.2,
                minWidth: 150,
                align: "center",
                headerAlign: "center",
                headerClassName: "leave-col-accrual",
                cellClassName: "leave-col-accrual",
                renderCell: (params) => {
                    const { usedFromYear, usedFromYearDetails } = params.row || {};
                    const daySeconds = resolveDaySeconds(
                        params.row,
                        numberOfHoursOneDay
                    );
                    const totalSeconds =
                        sumDetailsSeconds(usedFromYearDetails) ||
                        Math.round(Number(usedFromYear ?? 0) * daySeconds);

                    return (
                        <UsedTimeFormulaTooltip
                            details={usedFromYearDetails}
                            totalDays={usedFromYear}
                        >
                            <DaysWithHoursCell
                                days={usedFromYear}
                                totalSeconds={totalSeconds}
                                t={t}
                                timeFormat={genaralSettingTimeFormat}
                            />
                        </UsedTimeFormulaTooltip>
                    );
                },
            },
            {
                field: "totalEntitlementYear",
                headerName: t("leave.accrued-from-months"),
                flex: 1.2,
                minWidth: 160,
                align: "center",
                headerAlign: "center",
                headerClassName: "leave-col-accrual",
                cellClassName: "leave-col-accrual",
                renderCell: (params) => {
                    const { leaveTypeId, totalEntitlementYear } = params.row || {};
                    const daySeconds = resolveDaySeconds(
                        params.row,
                        numberOfHoursOneDay
                    );
                    const draft = draftLeaveTypes[leaveTypeId];
                    const days = draft?.totalEntitlementYear ?? totalEntitlementYear ?? 0;
                    const totalSeconds = Math.round(Number(days) * daySeconds);

                    if (!isAdmin) {
                        return (
                            <DaysWithHoursCell
                                days={days}
                                totalSeconds={totalSeconds}
                                t={t}
                                timeFormat={genaralSettingTimeFormat}
                            />
                        );
                    }

                    return (
                        <EntitlementEditableCell
                            value={days}
                            unitLabel={t("common.day").toLowerCase()}
                            hoursLabel={formatHoursMinutesLabel(
                                totalSeconds,
                                genaralSettingTimeFormat
                            )}
                            onChange={(next) =>
                                handleLeaveTypeFieldChange(
                                    leaveTypeId,
                                    "totalEntitlementYear",
                                    next
                                )
                            }
                        />
                    );
                },
            },
        ],
        [
            t,
            i18n.language,
            isAdmin,
            draftLeaveTypes,
            numberOfHoursOneDay,
            genaralSettingTimeFormat,
        ]
    );

    const columnGroupingModel = useMemo(
        () => {
            // Năm đang chọn = năm hiện tại → "năm trước" / "năm nay"
            // Năm cũ → chỉ "Điều chuyển" / "Tích lũy" + năm
            const isCurrentYear = year === currentYear;

            return [
                {
                    groupId: "carryFromPrevYear",
                    headerName: isCurrentYear
                        ? t("leave.carry-from-prev-year", { year: year - 1 })
                        : t("leave.carry-from-year", { year: year - 1 }),
                    headerAlign: "center",
                    align: "center",
                    headerClassName: "leave-group-carry",
                    children: [
                        { field: "usedFromCarry" },
                        { field: "carryIn" },
                    ],
                },
                {
                    groupId: "accruedThisYear",
                    headerName: isCurrentYear
                        ? t("leave.accrued-this-year", { year })
                        : t("leave.accrued-year", { year }),
                    headerAlign: "center",
                    align: "center",
                    headerClassName: "leave-group-accrual",
                    children: [
                        { field: "usedFromYear" },
                        { field: "totalEntitlementYear" },
                    ],
                },
            ];
        },
        [t, year, currentYear]
    );

    const filteredRows = useMemo(
        () => leaveByUser.filter((row) => row.leaveTypeId !== 1),
        [leaveByUser]
    );
    const hasRows = filteredRows.length > 0;
    const gridHeight = hasRows ? "41vh" : "41vh";

    // Giống bảng leave: X ngày + (HH:mm:ss); quy đổi theo snapshot hoặc setting 1 ngày không phép
    const unpaidMonthValues = useMemo(
        () =>
            Array.from({ length: 12 }, (_, index) => {
                const month = index + 1;
                const item = unpaidByMonth[month] ?? {
                    numOfTime: "00:00:00",
                    hoursPerLeaveDay: "",
                };
                const numOfTime = item?.numOfTime || "00:00:00";
                const hoursPerDayRaw =
                    item?.hoursPerLeaveDay ||
                    numberOfHoursOneDay ||
                    "08:00:00";
                const daySeconds = parseTimeToSeconds(hoursPerDayRaw);
                // Không có giờ hợp lệ thì quy đổi theo 8 giờ = 1 ngày
                const hoursPerLeaveDay =
                    daySeconds > 0 ? hoursPerDayRaw : "08:00:00";
                const totalSeconds = parseTimeToSeconds(numOfTime);
                const days =
                    daySeconds > 0
                        ? calcUnpaidDaysFromTime(numOfTime, hoursPerLeaveDay)
                        : totalSeconds / DEFAULT_DAY_SECONDS;

                return {
                    numOfTime,
                    hoursPerLeaveDay,
                    days: formatOneDecimal(days),
                    totalSeconds,
                };
            }),
        [unpaidByMonth, numberOfHoursOneDay]
    );

    /* ===================== RENDER ===================== */
    return (
        <Box className="relative flex flex-col gap-4" sx={{ minHeight: "70.5vh" }}>
            <CustomSpinner loading={loading} />

            {/* Header */}
            <Box className="flex items-center justify-between">
                {/* Year Select */}
                <YearPickerPopup
                    value={year}
                    onChange={(newYear) => setYear(newYear)}
                />

                <Box className="flex gap-2">
                    <div className="flex rounded-md border border-[#DBE8FF] overflow-hidden bg-[#F5F8FF] h-10">
                        <button
                            onClick={handleOpenLeaveSummary}
                            className="flex items-center gap-2 px-4 py-2 text-[#0057FF] hover:bg-[#F5F8FF] h-[38px]"
                        >
                            <InfoOutlinedIcon className="!w-5 !h-5" />
                            {t("button.detail")}
                        </button>
                    </div>
                </Box>
            </Box>

            <div className="flex flex-col gap-4 p-2 border border-[#E5E7EB] rounded-lg justify-between">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <p className="font-semibold">{t("common.totalUnpaid")}</p>
                        <DefaultTooltip
                            title={t("common.totalUnpaid")}
                            readMore={false}
                            content={
                                <div className="flex flex-col gap-3">
                                    <p>{t("common.unpaid-hours-explain")}</p>
                                </div>
                            }
                        >
                            <div>
                                <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                            </div>
                        </DefaultTooltip>
                    </div>
                    <div className="p-3 border border-[#E5E7EB] rounded-lg flex items-center justify-center">
                        <FaClock className="text-[#0057FF] text-[20px]" />
                    </div>
                </div>


                {/* Bảng nghỉ không phép theo tháng */}
                <div>
                    {/* Outer luôn giữ 10px cho scrollbar → hover không đẩy layout */}
                    <Box sx={{ pb: "10px" }}>
                        <Box
                            className="border border-[#E5E7EB] rounded-lg overflow-x-auto"
                            sx={{
                                mb: "-10px",
                                pb: "3px",
                                scrollbarWidth: "thin",
                                scrollbarColor: "transparent transparent",
                                "&::-webkit-scrollbar": {
                                    height: 10,
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "transparent",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    background: "transparent",
                                    borderRadius: 8,
                                },
                                "&:hover": {
                                    scrollbarColor: "#BDBDBD transparent",
                                    "&::-webkit-scrollbar-thumb": {
                                        background: "#BDBDBD",
                                    },
                                },
                                "&:focus-within": {
                                    scrollbarColor: "#BDBDBD transparent",
                                    "&::-webkit-scrollbar-thumb": {
                                        background: "#BDBDBD",
                                    },
                                },
                            }}
                        >
                            <table className="w-full min-w-[960px] table-fixed border-collapse">
                                <colgroup>
                                    {MONTH_KEYS.map((monthKey) => (
                                        <col key={monthKey} style={{ width: `${100 / 12}%` }} />
                                    ))}
                                </colgroup>
                                <thead>
                                    <tr className="bg-[#F9FAFB]">
                                        {MONTH_KEYS.map((monthKey) => (
                                            <th
                                                key={monthKey}
                                                title={t(`months.${monthKey}`)}
                                                className="px-1 py-3 text-center text-xs font-semibold text-[#525252] border-b border-[#E5E7EB] truncate"
                                            >
                                                {t(`months.${monthKey}`)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        {unpaidMonthValues.map((item, index) => (
                                            <td
                                                key={MONTH_KEYS[index]}
                                                className="px-0.5 py-2 text-center border-b border-[#E5E7EB] align-middle overflow-hidden"
                                            >
                                                <UnpaidHoursFormulaTooltip
                                                    numOfTime={item.numOfTime}
                                                    hoursPerLeaveDay={item.hoursPerLeaveDay}
                                                    days={item.days}
                                                >
                                                    <DaysWithHoursCell
                                                        days={item.days}
                                                        totalSeconds={item.totalSeconds}
                                                        t={t}
                                                        timeFormat={genaralSettingTimeFormat}
                                                        compact
                                                    />
                                                </UnpaidHoursFormulaTooltip>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    </Box>
                </div>

            </div>



            {/* Table: nhóm Điều chuyển (xanh dương) / Tích lũy (xanh lá) theo mẫu */}
            <StyledDataGrid
                rows={filteredRows}
                getRowId={(row) => row.leaveTypeId}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                hideFooter
                disableRowSelectionOnClick
                columnHeaderHeight={48}
                rowHeight={56}
                slots={{
                    pagination: CustomPagination,
                    noRowsOverlay: NoRowOverlay,
                    columnsPanel: CustomColumnsPanel,
                }}
                sx={{
                    height: gridHeight,
                    overflow: "hidden",
                    ".MuiDataGrid-main": {
                        height: gridHeight,
                    },
                    "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
                    {
                        display: hasRows ? "none !important" : "none !important",
                    },

                    "& .MuiDataGrid-scrollbarFiller": {
                        display: "none !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        paddingRight: 0,
                    },
                    "& .MuiDataGrid-topContainer": {
                        paddingRight: 0,
                    },
                    "&": {
                        "--DataGrid-scrollbarSize": "0px",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        overflow: hasRows ? "auto !important" : "hidden !important",
                        scrollbarWidth: hasRows ? "none" : "none",
                    },
                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                        width: 0,
                        height: 0,
                    },
                    ...(hasRows && {
                        "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
                        {
                            scrollbarWidth: "thin",
                        },
                        "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
                        {
                            width: 10,
                            height: 10,
                        },
                    }),
                    '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                        display: "none !important",
                    },
                    "& .MuiDataGrid-cell": {
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                    },
                    "& .MuiDataGrid-cell--textCenter": {
                        justifyContent: "center",
                    },
                    "& .entitlement-edit-input": {
                        pointerEvents: "auto",
                        caretColor: "auto",
                        userSelect: "text",
                    },
                    "& .MuiDataGrid-overlayWrapper": {
                        height: "100%",
                        minHeight: hasRows ? undefined : 236,
                    },
                    "& .MuiDataGrid-overlay": {
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    },
                    // Nhóm Điều chuyển từ năm trước
                    // "& .leave-group-carry, & .leave-col-carry.MuiDataGrid-columnHeader": {
                    //     backgroundColor: "#EFF6FF",
                    //     color: "#1D4ED8",
                    // },
                    // "& .leave-col-carry.MuiDataGrid-cell": {
                    //     backgroundColor: "#F8FBFF",
                    // },
                    // // Nhóm Tích lũy năm nay
                    // "& .leave-group-accrual, & .leave-col-accrual.MuiDataGrid-columnHeader": {
                    //     backgroundColor: "#ECFDF5",
                    //     color: "#047857",
                    // },
                    // "& .leave-col-accrual.MuiDataGrid-cell": {
                    //     backgroundColor: "#F7FDF9",
                    // },
                }}
            />
            {isAdmin && isDirty && (
                <Box className="flex justify-end gap-2">
                    <ControlpanelSecondaryButton
                        onClick={handleCancelEntitlements}
                        disabled={isSaving}
                    >
                        {t("button.cancel")}
                    </ControlpanelSecondaryButton>
                    <ControlpanelPrimaryButton
                        onClick={handleSaveEntitlements}
                        disabled={isSaving}
                    >
                        {t("button.save")}
                    </ControlpanelPrimaryButton>
                </Box>
            )}
            {openEditEntitlement && (
                <EditEntitlementForm
                    leaveByUser={leaveByUser}
                    open={openEditEntitlement}
                    handleClose={handleCloseEditEntitlement}
                    handleRefreshTable={handleRefreshTable}
                    userId={userId}
                    year={year}
                    onReload={fetchLeaveByUserData}
                />
            )}
            {openLeaveSummary && (
                <LeaveSummary
                    open={openLeaveSummary}
                    handleClose={handleCloseLeaveSummary}
                    userId={userId}
                    year={year}
                />
            )}
        </Box>
    );
};

export default LeaveManagement;
